import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useCartStore } from '@/app/store/cartStore';
import { useUserProfile } from '@/hooks/useUserProfile';

/**
 * Hook to get and manage cart count
 * Automatically fetches cart count when user is authenticated
 * Uses localStorage caching for fast initial render
 * Refetches on every route change (stale-while-revalidate)
 * @returns Object with cartCount, isLoading, error, and refetch function
 */
export function useCartCount() {
  const { isAuthenticated } = useUserProfile();
  const { cartCount, isLoading, error, fetchCartCount, resetCartCount, clearCacheAndRefetch } = useCartStore();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated) {
      // Fetch on every route change - stale-while-revalidate pattern
      // Shows localStorage value immediately, then verifies with API
      fetchCartCount();
    } else {
      // Reset count and clear localStorage when user logs out
      resetCartCount();
    }
    // Include location.pathname to refetch on route changes
  }, [isAuthenticated, location.pathname, fetchCartCount, resetCartCount]);

  return {
    cartCount: cartCount ?? 0, // Default to 0 if null
    isLoading,
    error,
    refetch: clearCacheAndRefetch, // Always clear cache and refetch fresh from API
  };
}

